import { useCallback, useMemo, useState } from 'react';

import {
  BASE_CURRENCY,
  formatMoney,
  formatRateLine,
  getCurrency,
  isBaseCurrency,
  isSupportedCurrency,
  type CurrencyMeta,
} from '../lib/currency';

const STORAGE_KEY = 'geofleet_display_currency';

function readStoredCurrency(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isSupportedCurrency(stored) ? (stored as string).toUpperCase() : null;
  } catch {
    return null;
  }
}

export type DisplayCurrency = {
  currency: string;
  meta: CurrencyMeta;
  setCurrency: (code: string) => void;
  isBase: boolean;
  /** "1 USD ≈ AED 3.67", or null when showing USD. */
  rateLine: string | null;
  /** Format a USD list price in the active display currency. */
  format: (amountUsd: number) => string;
};

/**
 * Which currency list prices are shown in. USD is always the billed currency —
 * this only changes what the visitor reads.
 *
 * Defaults to USD; an explicit pick wins and is remembered.
 */
export function useDisplayCurrency(): DisplayCurrency {
  const [picked, setPicked] = useState<string | null>(() => readStoredCurrency());

  const currency = picked ?? BASE_CURRENCY;

  const setCurrency = useCallback((code: string) => {
    if (!isSupportedCurrency(code)) return;
    const next = code.toUpperCase();
    setPicked(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage blocked — the pick still applies for this page view */
    }
  }, []);

  const format = useCallback((amountUsd: number) => formatMoney(amountUsd, currency), [currency]);

  return useMemo(
    () => ({
      currency,
      meta: getCurrency(currency),
      setCurrency,
      isBase: isBaseCurrency(currency),
      rateLine: formatRateLine(currency),
      format,
    }),
    [currency, setCurrency, format]
  );
}
